import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Input, Skeleton, Tooltip, cn, cssVars } from "@sagot/ui";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../session/workspace-context";
import { PageHeader } from "../shared/page-header";
import { useAgentList, useAudienceLabels } from "../shared/queries";
import { TypedConfirmDialog } from "../shared/typed-confirm-dialog";
import type { AgentSummary } from "../shared/types";
import { AgentCard, type AgentCardPermissions } from "./agent-card";
import { CREATE_HASH, CREATE_QUERY, filterAgents, planState, type PlanState } from "./agent-model";
import { CreateAgentDialog } from "./create-agent-dialog";
import { useAgentActions } from "./use-agent-actions";

function usePlanName(): string {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan ?? "";
  return plan ? t(`console.plans.${plan}`, { defaultValue: plan }) : "";
}

function PlanLimitCard({ plan, planName }: { plan: PlanState; planName: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <section className="card card-body flex flex-col gap-4 sm:flex-row sm:items-center" aria-label={t("build.agents.plan.meterLabel")}>
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-ink">
        <Icon name="layers" className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-bold">
            {plan.limit === null
              ? t("build.agents.plan.usedUnlimited", { used: plan.used, plan: planName })
              : t("build.agents.plan.used", { used: plan.used, limit: plan.limit, plan: planName })}
          </span>
          {plan.remaining === null ? null : (
            <span className={cn("text-xs tabular-nums", plan.full ? "font-semibold text-warning" : "text-fg-subtle")}>
              {plan.full ? t("build.agents.plan.full") : t("build.agents.plan.remaining", { count: plan.remaining })}
            </span>
          )}
        </div>
        {plan.limit === null ? null : (
          <div
            className="mt-2 h-1.5 rounded-pill bg-sunken"
            role="progressbar"
            aria-label={t("build.agents.plan.meterLabel")}
            aria-valuemin={0}
            aria-valuemax={plan.limit}
            aria-valuenow={plan.used}
          >
            <div className={cn("h-1.5 w-[var(--geo-width)] rounded-pill", plan.full ? "bg-accent" : "bg-primary")} style={cssVars({ "--geo-width": `${String(plan.percent)}%` })} />
          </div>
        )}
        <p className="mt-2 text-xs text-fg-muted">{t("build.agents.plan.body")}</p>
      </div>
      <Button asChild variant="secondary" size="sm" className="shrink-0">
        <AppLink href={consoleRoutes.billing(summary.slug)}>{t("build.agents.plan.compare")}</AppLink>
      </Button>
    </section>
  );
}

function CreateTile({ plan, planName, onCreate }: { plan: PlanState; planName: string; onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onCreate}
      disabled={plan.full}
      className="flex min-h-72 flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed border-line-strong bg-surface-2 p-6 text-center transition-colors hover:border-primary hover:bg-primary-soft disabled:cursor-not-allowed disabled:opacity-70"
    >
      <span className="inline-flex size-11 items-center justify-center rounded-pill border border-line bg-surface text-primary-ink">
        <Icon name="plus" className="size-5" />
      </span>
      <span className="text-base font-bold">{t("build.agents.tile.title")}</span>
      <span className="max-w-60 text-xs text-fg-muted">{t("build.agents.tile.body")}</span>
      <span className="text-[11px] font-semibold text-fg-subtle">
        {plan.full ? t("build.agents.tile.full") : plan.remaining === null ? null : t("build.agents.tile.slots", { count: plan.remaining, plan: planName })}
      </span>
    </button>
  );
}

function AccessInfo() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const items = [
    { icon: "database", title: "kbTitle", body: "kbBody" },
    { icon: "lock", title: "accessTitle", body: "accessBody" },
    { icon: "plug", title: "channelsTitle", body: "channelsBody" },
  ] as const;
  return (
    <section className="card" aria-labelledby="agents-access-info">
      <div className="card-header">
        <h2 id="agents-access-info" className="card-title flex items-center gap-2">
          <Icon name="shield-check" className="size-4 text-primary-ink" />
          {t("build.agents.info.title")}
        </h2>
        <AppLink href={consoleRoutes.audiences(summary.slug)} className="text-xs font-semibold text-primary-ink hover:underline">
          {t("build.agents.info.link")}
        </AppLink>
      </div>
      <div className="card-body grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
        {items.map((item) => (
          <div key={item.title} className="flex gap-3">
            <Icon name={item.icon} className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
            <p className="text-fg-muted">
              <strong className="text-fg">{t(`build.agents.info.${item.title}`)}</strong> {t(`build.agents.info.${item.body}`)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="card space-y-4 p-5">
          <div className="flex gap-3">
            <Skeleton className="size-11 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
          <Skeleton className="h-4 w-48" />
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }, (_, j) => <Skeleton key={j} className="h-14" />)}
          </div>
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}

/** `#new` / `?new=1` opens the create dialog (shell "Create agent" link). */
function useCreateFromUrl(enabled: boolean): [boolean, (open: boolean) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const wanted = location.hash === CREATE_HASH || new URLSearchParams(location.search).has(CREATE_QUERY);
  const [manual, setManual] = useState(false);
  const change = (next: boolean) => {
    setManual(next);
    if (!next && wanted) void navigate({ pathname: location.pathname, search: "", hash: "" }, { replace: true });
  };
  const open = manual || (wanted && enabled);
  return [open, change];
}

export function AgentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const message = useErrorMessage();
  const list = useAgentList();
  const permissions = usePermissions();
  const audienceLabel = useAudienceLabels();
  const actions = useAgentActions();
  const planName = usePlanName();
  const [query, setQuery] = useState("");
  const [deleting, setDeleting] = useState<AgentSummary | null>(null);
  const writable = !permissions.readOnly;
  const can: AgentCardPermissions = {
    create: writable && permissions.can("agents.create", { anywhere: true }),
    edit: writable && permissions.can("agents.edit", { anywhere: true }),
    publish: writable && permissions.can("agents.publish", { anywhere: true }),
    remove: writable && permissions.can("agents.delete", { anywhere: true }),
  };
  const [createOpen, setCreateOpen] = useCreateFromUrl(can.create);
  const plan = planState(list.data?.plan);
  const agents = list.data?.items ?? [];
  const shown = filterAgents(agents, query);

  const createButton = (
    <Button onClick={() => { setCreateOpen(true); }} disabled={!can.create || plan.full}>
      <Icon name="plus" />
      {t("build.agents.create")}
    </Button>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={t("build.agents.title")}
        subtitle={t("build.agents.subtitle")}
        actions={
          <>
            <div className="relative">
              <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
              <Input type="search" aria-label={t("build.agents.searchLabel")} placeholder={t("build.agents.search")} className="h-9 w-56 pl-9" value={query} onChange={(e) => { setQuery(e.target.value); }} />
            </div>
            {can.create ? createButton : <Tooltip content={t("build.noPermission")}><span>{createButton}</span></Tooltip>}
          </>
        }
      />
      {list.isPending ? <Skeleton className="h-28 w-full rounded-card" /> : null}
      {list.data ? <PlanLimitCard plan={plan} planName={planName} /> : null}
      {list.isPending ? <GridSkeleton /> : null}
      {list.isError ? <ErrorState title={t("build.loadError")} description={t("build.loadErrorBody")} onRetry={() => void list.refetch()} retryLabel={t("common.retry")} /> : null}
      {list.data && agents.length === 0 ? (
        <EmptyState icon="bot" title={t("build.agents.empty.title")} description={t("build.agents.empty.body")} action={can.create ? createButton : undefined} />
      ) : null}
      {list.data && agents.length > 0 && shown.length === 0 ? (
        <EmptyState
          icon="search"
          title={t("build.agents.empty.noMatch", { query })}
          description={t("build.agents.empty.noMatchBody")}
          action={<Button variant="secondary" onClick={() => { setQuery(""); }}>{t("build.agents.empty.clear")}</Button>}
        />
      ) : null}
      {shown.length > 0 ? (
        <section className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3" aria-label={t("build.agents.title")}>
          {shown.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              audienceLabel={audienceLabel}
              can={can}
              onDuplicate={(a) => { actions.duplicate.mutate(a); }}
              onToggleStatus={(a) => { actions.setStatus.mutate({ agent: a, status: a.status === "live" ? "paused" : "live" }); }}
              onPublish={(a) => { actions.publish.mutate(a); }}
              onDelete={setDeleting}
            />
          ))}
          {can.create && !query ? <CreateTile plan={plan} planName={planName} onCreate={() => { setCreateOpen(true); }} /> : null}
        </section>
      ) : null}
      {list.data ? <AccessInfo /> : null}
      <CreateAgentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        agents={agents}
        plan={plan}
        planName={planName}
        onCreated={(agent) => void navigate(consoleRoutes.knowledge(summary.slug, agent.id))}
      />
      <TypedConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
            actions.remove.reset();
          }
        }}
        title={t("build.agents.deleteDialog.title", { name: deleting?.name ?? "" })}
        body={t("build.agents.deleteDialog.body")}
        confirmLabel={t("build.agents.deleteDialog.confirmLabel")}
        expected={t("build.agents.deleteDialog.confirmWord")}
        submitLabel={t("build.agents.deleteDialog.submit")}
        pending={actions.remove.isPending}
        error={actions.remove.isError ? message(actions.remove.error) : null}
        onConfirm={(confirm) => {
          if (deleting) actions.remove.mutate({ agent: deleting, confirm }, { onSuccess: () => { setDeleting(null); } });
        }}
      />
    </div>
  );
}
